import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import { ResponsivePie } from '@nivo/pie';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import AmountField from '../../../components/AmountField';
// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

const NPS = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-equity-nps', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemeTransactions, setSchemeTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editNavValue, setEditNavValue] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');
  
  // Form state
  const [schemeName, setSchemeName] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [navValue, setNavValue] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');
  
  // User's saved scheme names (will be populated from existing investments)
  const [userSchemeNames, setUserSchemeNames] = useState([]);

  // Group investments by scheme name
  const groupInvestmentsByScheme = (investments) => {
    const schemeMap = new Map();
    
    investments.forEach(investment => {
      const { name, value, units } = investment;
      
      if (schemeMap.has(name)) {
        const scheme = schemeMap.get(name);
        scheme.value += value;
        scheme.units += parseFloat(units || 0);
        scheme.transactions.push(investment);
      } else {
        schemeMap.set(name, {
          name,
          value,
          units: parseFloat(units || 0),
          transactions: [investment]
        });
      }
    });
    
    // Convert map to array
    return Array.from(schemeMap.values());
  };

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch NPS investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching NPS investments for user ID:', user.id);
      
      // Fetch from Supabase
      const { data, error } = await supabase
        .from('nps_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        console.error('Error fetching NPS investments:', error);
        throw error;
      }
      
      console.log('Fetched NPS data:', data);
      
      // Process data - format and group by scheme name
      if (data && data.length > 0) {
        // Extract unique scheme names for autocomplete
        const uniqueSchemeNames = [...new Set(data.map(item => item.name))];
        setUserSchemeNames(uniqueSchemeNames);
        
        // Format data for display
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          navValue: item.nav_value,
          purchaseDate: item.purchase_date,
          units: item.units
        }));
        
        // Group investments by scheme name
        const groupedInvestments = groupInvestmentsByScheme(formattedData);
        setInvestments(groupedInvestments);
        
        // Calculate total value
        const total = groupedInvestments.reduce((sum, scheme) => sum + scheme.value, 0);
        setTotalValue(total);
        console.log('Total NPS value:', total);
      } else {
        console.log('No NPS investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserSchemeNames([]);
      }
    } catch (error) {
      console.error('Error processing NPS investments:', error);
      // Fall back to empty state
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Calculate units when NAV and amount invested changes
  useEffect(() => {
    if (navValue && amountInvested && parseFloat(navValue) > 0 && parseFloat(amountInvested) > 0) {
      setUnits((parseFloat(amountInvested) / parseFloat(navValue)).toFixed(4));
    } else {
      setUnits('');
    }
  }, [navValue, amountInvested]);

  // Calculate units when NAV and amount invested changes for edit modal
  useEffect(() => {
    if (editNavValue && editAmountInvested && parseFloat(editNavValue) > 0 && parseFloat(editAmountInvested) > 0) {
      setEditUnits((parseFloat(editAmountInvested) / parseFloat(editNavValue)).toFixed(4));
    } else {
      setEditUnits('');
    }
  }, [editNavValue, editAmountInvested]);
  
  // Load data on component mount
  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  
  const handleReset = () => {
    setSchemeName('');
    setPurchaseDate(new Date());
    setNavValue('');
    setUnits('');
    setAmountInvested('');
  };
  
  // Transaction history modal handlers
  const handleOpenTransactionHistory = (scheme) => {
    setSelectedScheme(scheme);
    setSchemeTransactions(scheme.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedScheme(null);
    setSchemeTransactions([]);
  };
  
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditNavValue(currentTransaction.navValue ? currentTransaction.navValue.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    setEditUnits(currentTransaction.units ? currentTransaction.units.toString() : '');
    
    setOpenEditModal(true);
  };
  
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };
  
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!schemeName || !purchaseDate || !navValue || !amountInvested) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the scheme name (handle different possible formats)
      let schemeNameValue = '';
      if (typeof schemeName === 'string') {
        schemeNameValue = schemeName;
      } else if (schemeName && typeof schemeName === 'object') {
        // If it's an object with a label property (from old data format)
        schemeNameValue = schemeName.label || '';
      }
      
      if (!schemeNameValue) {
        alert('Please enter a valid scheme name');
        setLoading(false);
        return;
      }
      
      // Prepare the data for Supabase
      const newInvestment = {
        name: schemeNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        nav_value: parseFloat(navValue),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        user_id: user.id
      };

      console.log('Creating new NPS investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('nps_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editNavValue || !editAmountInvested || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        nav_value: parseFloat(editNavValue),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('nps_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedScheme && openHistoryModal) {
        // Get the latest transactions for this scheme
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('nps_investments')
          .select('*')
          .eq('name', selectedScheme.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            navValue: item.nav_value,
            purchaseDate: item.purchase_date,
            units: item.units
          }));
          
          setSchemeTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('nps_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedScheme && openHistoryModal) {
        // Get the latest transactions for this scheme
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('nps_investments')
          .select('*')
          .eq('name', selectedScheme.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              navValue: item.nav_value,
              purchaseDate: item.purchase_date,
              units: item.units
            }));
            
            setSchemeTransactions(formattedTransactions);
          } else {
            // No transactions left for this scheme, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Component render
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/investments/equity')}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box data-tour="inv-prod-summary">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                National Pension System (NPS)
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <TourButton onClick={tour.start} />
            <Tooltip title="Refresh Data">
              <IconButton 
                data-tour="inv-nps-refresh"
                onClick={fetchInvestments} 
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button 
              data-tour="inv-prod-add"
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 2,
              }}
            >
              Add Investment
            </Button>
          </Box>
        </Box>

        {/* Modal for adding new investment */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="add-investment-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Add NPS Investment
            </Typography>
            
            <Stack spacing={3}>
              <Autocomplete
                value={schemeName}
                onChange={(event, newValue) => setSchemeName(newValue)}
                freeSolo
                autoHighlight
                options={userSchemeNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userSchemeNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing schemes
                  else {
                    return "Your Scheme Names";
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Name of Scheme" 
                    required 
                    fullWidth
                    placeholder="Enter or select scheme name"
                    helperText="You can type a new scheme name or select from your existing schemes"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV"
                value={navValue}
                onChange={(e) => setNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Enter the NAV (Net Asset Value) at purchase"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={units}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    handleReset();
                    handleCloseModal();
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {loading ? <CircularProgress size={24} /> : "Save Investment"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Transaction Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleTransactionMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150, borderRadius: 2 }
          }}
        >
          <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" color="primary" />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
            <DeleteIcon fontSize="small" color="error" />
            Delete
          </MenuItem>
        </Menu>

        {/* Edit Transaction Modal */}
        <Modal
          open={openEditModal}
          onClose={handleEditModalClose}
          aria-labelledby="edit-transaction-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Edit Transaction
            </Typography>
            
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Scheme: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV Value"
                value={editNavValue}
                onChange={(e) => setEditNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={editUnits}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  disabled={loading}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {loading ? <CircularProgress size={24} /> : "Update Transaction"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          aria-labelledby="delete-transaction-dialog"
        >
          <DialogTitle id="alert-dialog-title">
            Confirm Delete Transaction
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogContentText>
            {currentTransaction && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Details:
                </Typography>
                <Typography variant="body2">
                  Scheme: {currentTransaction.name}<br />
                  Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                  Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(currentTransaction.value)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleDeleteDialogClose} 
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteTransaction} 
              variant="contained" 
              color="error"
              sx={{ borderRadius: 2, textTransform: 'none' }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal for viewing transaction history */}
        <Modal
          open={openHistoryModal}
          onClose={handleCloseHistoryModal}
          aria-labelledby="transaction-history-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  {selectedScheme?.name}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Invested Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(selectedScheme?.value || 0)} | 
                  Total Units: {(selectedScheme?.units || 0).toFixed(2)} | 
                  Average Price: ₹{(() => {
                    if (schemeTransactions.length === 0) return '0.00';
                    const totalAmount = schemeTransactions.reduce((sum, tx) => sum + tx.value, 0);
                    const totalUnits = schemeTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                    return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                  })()}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseHistoryModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Chip 
                icon={<ReceiptIcon fontSize="small" />} 
                label="Transaction History" 
                color="primary" 
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>NAV (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Units</TableCell>
                    <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schemeTransactions.length > 0 ? (
                    schemeTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {transaction.purchaseDate 
                            ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.navValue 
                            ? parseFloat(transaction.navValue).toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(transaction.value)}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.units 
                            ? parseFloat(transaction.units).toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No transaction history available
                      </TableCell>
                    </TableRow>
                  )}
                  {schemeTransactions.length > 0 && (
                    <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(schemeTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {schemeTransactions
                          .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                          .toFixed(2)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={handleCloseHistoryModal}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : investments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No NPS Investments Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              Start by adding your first NPS investment using the 'Add Investment' button.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Add Your First Investment
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper
                data-tour="inv-prod-holdings"
                sx={{
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: 'var(--fx-card-shadow)',
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Scheme Details
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary' }}>Scheme Name</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>Value</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>Allocation</TableCell>
                        <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {investments.map((investment, index) => (
                        <TableRow
                          key={investment.name}
                          onClick={() => handleOpenTransactionHistory(investment)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: COLORS[index % COLORS.length],
                                  mr: 2,
                                }}
                              />
                              {investment.name}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(investment.value)}
                          </TableCell>
                          <TableCell align="right">
                            {((investment.value / totalValue) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View transactions">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(totalValue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          100%
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4} data-tour="inv-prod-charts">
              <Paper
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: 'var(--fx-card-shadow)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Distribution
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                  {pieChartData.length > 0 && (
                    <PieChart colors={chartPalette}
                      series={[
                        {
                          data: pieChartData,
                          highlightScope: { faded: 'global', highlighted: 'item' },
                          faded: { innerRadius: 30, additionalRadius: -30 },
                        },
                      ]}
                      height={450}
                      legend={{
                        hidden: true
                      }}
                      margin={{ top: 10, bottom: 50, left: 20, right: 20 }}
                      slotProps={{
                        legend: {
                          direction: 'row',
                          position: { vertical: 'bottom', horizontal: 'middle' },
                          padding: { top: 10, bottom: 0, left: 0, right: 0 },
                          itemMarkWidth: 8,
                          itemMarkHeight: 8,
                          markGap: 5,
                          itemGap: 15,
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 2,
                          },
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        <GuidedTour steps={TOURS['inv-equity-nps']} open={tour.open} onClose={tour.close} />
      </Box>
    </LocalizationProvider>
  );
};

export default NPS;